import { useId, useMemo, useState } from 'react';

import type { SearchFormValues, SectorGroup } from '../../types';
import { accentuate } from '../../lib/accents';
import { MAX_RADIUS_KM, MIN_RADIUS_KM } from '../App';
import { IconArrow, IconPin, IconShop } from '../components/Icons';
import { SectorSheet } from './SectorSheet';

interface SearchFormProps {
  values: SearchFormValues;
  onValuesChange: (values: SearchFormValues) => void;
  fieldErrors: Record<string, string>;
  onSubmit: () => void;
  isLoading: boolean;
  sectorGroups: readonly SectorGroup[];
}

/**
 * L'écran de recherche.
 *
 * Il ne cohabite pas avec les résultats : une fois la recherche lancée, il
 * cède la place et se retrouve par « Modifier ». Ce formulaire fait sept
 * contrôles — le garder au-dessus de la liste obligerait à le dépasser au
 * défilement à chaque consultation, des dizaines de fois par session.
 *
 * L'ordre suit la façon dont la question se pose à voix haute : où, jusqu'où,
 * qui. L'adresse d'abord parce que c'est la seule saisie obligatoire.
 */
export function SearchForm(props: SearchFormProps) {
  const addressId = useId();
  const queryId = useId();
  const radiusId = useId();
  const ignoredId = useId();
  const ecoId = useId();
  const [sectorOpen, setSectorOpen] = useState(false);

  const { values, onValuesChange, fieldErrors, sectorGroups } = props;

  /** Le métier retenu, et le groupe auquel il appartient. */
  const selected = useMemo(
    () =>
      sectorGroups
        .flatMap((group) => group.sectors.map((sector) => ({ group, sector })))
        .find((pair) => pair.sector.id === values.sectorId) ?? null,
    [sectorGroups, values.sectorId],
  );

  const multiCategory = values.sectorId === '' && values.query.trim() === '';

  /**
   * Le coût annoncé doit rester vrai dans les trois cas, sinon la promesse
   * « on te dit avant de dépenser » ne vaut plus rien.
   *
   * « Toutes catégories » passe par Nearby Search, qui ne pagine pas : le mode
   * économique n'y change donc rien, et la case est désactivée plutôt que
   * laissée à mentir.
   */
  const cost = multiCategory
    ? '≈ 6 requêtes Google'
    : values.ecoMode
      ? '1 requête Google'
      : '1 à 3 requêtes Google';

  const radius = Number.isFinite(values.radiusKm) ? values.radiusKm : MIN_RADIUS_KM;

  return (
    <form
      className="stack"
      onSubmit={(event) => {
        event.preventDefault();
        if (!props.isLoading) props.onSubmit();
      }}
      noValidate
    >
      <div>
        <h1 className="title title-xl">
          Trouvez votre <em>prochain client</em>
        </h1>
        <p className="search-lead">Les commerces sans site web, autour de vous.</p>
      </div>

      {/* --- Où, et jusqu'où ------------------------------------------------ */}
      <div className="card">
        <label className="label" htmlFor={addressId}>
          Autour de
        </label>
        <div className="input-icon">
          <IconPin size={17} />
          <input
            id={addressId}
            className="input"
            type="text"
            name="address"
            autoComplete="off"
            placeholder="Cugnaux, France"
            maxLength={200}
            value={values.address}
            aria-invalid={Boolean(fieldErrors.address)}
            onChange={(event) => onValuesChange({ ...values, address: event.target.value })}
          />
        </div>

        <div className="radius-head">
          <label className="label" htmlFor={radiusId} style={{ margin: 0 }}>
            Rayon
          </label>
          <span className="radius-value" aria-hidden="true">
            {radius} km
          </span>
        </div>
        <input
          id={radiusId}
          className="range"
          type="range"
          name="radiusKm"
          min={MIN_RADIUS_KM}
          max={MAX_RADIUS_KM}
          step={1}
          value={radius}
          aria-valuetext={`${radius} kilomètres`}
          onChange={(event) =>
            onValuesChange({ ...values, radiusKm: event.target.valueAsNumber })
          }
        />
        <div className="radius-scale" aria-hidden="true">
          <span>{MIN_RADIUS_KM} km</span>
          <span>{MAX_RADIUS_KM} km</span>
        </div>
      </div>

      {fieldErrors.address !== undefined || fieldErrors.radiusKm !== undefined ? (
        <p className="notice notice-error" role="alert">
          {fieldErrors.address ?? fieldErrors.radiusKm}
        </p>
      ) : null}

      {/* --- Qui ------------------------------------------------------------ */}
      <span className="eyebrow" style={{ marginTop: 4 }}>
        Secteur
      </span>

      <button type="button" className="sector-button" onClick={() => setSectorOpen(true)}>
        <span className="sector-icon">
          <IconShop size={21} />
        </span>
        <span className="sector-body">
          <span className="sector-label">
            {selected === null ? 'Toutes catégories' : accentuate(selected.group.label)}
          </span>
          <span className="sector-detail">
            {selected === null
              ? 'Tous les métiers du rayon'
              : `${selected.group.sectors.length} métiers — actuellement ${accentuate(selected.sector.label).toLowerCase()}`}
          </span>
        </span>
        <span className="sector-cta">Choisir</span>
      </button>

      {/* Préciser le métier dans le groupe, ou chercher en toutes lettres.
          Les deux ne coexistent jamais : l'API prend l'un ou l'autre. */}
      {selected !== null ? (
        <div className="card-flat">
          <span className="eyebrow" style={{ marginBottom: 10 }}>
            Préciser · {accentuate(selected.group.label)}
          </span>
          <div className="chips-scroll">
            {selected.group.sectors.map((sector) => (
              <button
                key={sector.id}
                type="button"
                className="chip"
                aria-pressed={values.sectorId === sector.id}
                onClick={() => onValuesChange({ ...values, sectorId: sector.id, query: '' })}
              >
                {accentuate(sector.label)}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="field">
          <label className="label" htmlFor={queryId}>
            Activité <span className="hint">saisie libre, facultatif</span>
          </label>
          <input
            id={queryId}
            className="input"
            type="text"
            name="query"
            autoComplete="off"
            placeholder="paysagiste — vide = toutes catégories"
            maxLength={100}
            value={values.query}
            onChange={(event) =>
              onValuesChange({ ...values, query: event.target.value, sectorId: '' })
            }
          />
        </div>
      )}

      {/* --- Ce que ça va coûter -------------------------------------------- */}
      <div className="search-options">
        <label className="checkbox" htmlFor={ignoredId}>
          <input
            id={ignoredId}
            type="checkbox"
            checked={values.includeIgnored}
            onChange={(event) =>
              onValuesChange({ ...values, includeIgnored: event.target.checked })
            }
          />
          Afficher les ignorés
        </label>

        <label
          className="checkbox"
          htmlFor={ecoId}
          title={
            multiCategory
              ? 'Sans effet sur « toutes catégories » : ce mode ne pagine pas.'
              : 'Une seule page Google au lieu de trois. Moins de requêtes facturées, mais moins de résultats dans le rayon.'
          }
        >
          <input
            id={ecoId}
            type="checkbox"
            checked={values.ecoMode}
            disabled={multiCategory}
            onChange={(event) => onValuesChange({ ...values, ecoMode: event.target.checked })}
          />
          Mode économique
        </label>

        <span className="cost" style={{ marginLeft: 'auto' }}>
          {cost}
        </span>
      </div>

      <button type="submit" className="btn btn-go btn-block" disabled={props.isLoading}>
        {props.isLoading ? 'Recherche…' : 'Lancer la recherche'}
        {props.isLoading ? null : <IconArrow size={16} />}
      </button>

      {sectorOpen ? (
        <SectorSheet
          groups={sectorGroups}
          currentGroupId={selected?.group.id ?? null}
          onPick={(sectorId) => onValuesChange({ ...values, sectorId, query: '' })}
          onClose={() => setSectorOpen(false)}
        />
      ) : null}
    </form>
  );
}
